import React from 'react';
/* Fix: Import from react-router instead of react-router-dom for v7 unified package support */
import { Link } from 'react-router';
import { ChevronLeft, CheckCircle2 } from 'lucide-react';
import { TRANSLATIONS, SLUGS } from '../constants';
import { useSmartBack, useLanguageNavigate } from '../App';
import { getCurrentDay } from '../utils/gameUtils';

const Archive: React.FC = () => {
  const { handleBack, lang, prefix } = useSmartBack();
  const langNavigate = useLanguageNavigate();
  const currentDay = getCurrentDay();

  // Create an array for all past days including today's challenge
  const pastDays = Array.from({ length: currentDay + 1 }).map((_, i) => i).reverse();

  const getDayStatus = (dayIndex: number) => {
    const saved = localStorage.getItem(`MovieRiddle_Daily_Index_${dayIndex}`);
    if (!saved) return null;
    try {
      const state = JSON.parse(saved);
      return state.status as 'won' | 'lost' | 'playing';
    } catch (e) { return null; }
  };

  return (
    <div className="w-full max-w-4xl mx-auto py-12 px-6 animate-fade-in flex flex-col items-center">
      <div className="w-full mb-10 flex items-center justify-between">
        <button 
          onClick={handleBack} 
          className="text-zinc-400 hover:text-ink dark:hover:text-white transition-colors flex items-center gap-2 font-bold uppercase text-[10px] tracking-widest"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={3} />
          {TRANSLATIONS.backLabel[lang]}
        </button>
      </div>

      <div className="text-center mb-12 space-y-4">
        <h1 className="text-5xl sm:text-6xl font-heading uppercase tracking-widest text-ink dark:text-white">
          {TRANSLATIONS.archiveTitle[lang]}
        </h1>
        <p className="text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-[0.3em] text-[10px] sm:text-[11px]">
          {TRANSLATIONS.archiveSubtitle[lang]}
        </p>
      </div>

      {pastDays.length === 0 ? (
        <div className="py-20 text-center">
          <p className="text-zinc-400 dark:text-zinc-600 font-bold uppercase tracking-widest text-xs italic">
            {TRANSLATIONS.noArchive[lang]}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 sm:grid-6 w-full max-w-2xl mx-auto pb-20">
          {pastDays.map((dayIndex) => {
            const status = getDayStatus(dayIndex);
            const isCompleted = status === 'won' || status === 'lost';
            const hasPlayed = isCompleted;
            const publicDayNum = dayIndex + 1;
            
            const daySlug = SLUGS[lang].day || 'day';
            const dayUrl = `${prefix}/${daySlug}/${publicDayNum}`.replace(/\/+$/, '') || `/${daySlug}/${publicDayNum}`;

            return (
              <a 
                key={dayIndex}
                href={dayUrl}
                onClick={(e) => {
                  e.preventDefault();
                  langNavigate('day', publicDayNum.toString());
                }}
                className="group relative aspect-square bg-zinc-100 dark:bg-surface border border-zinc-200 dark:border-zinc-800 rounded-xl sm:rounded-2xl flex flex-col items-center justify-center transition-all duration-300 hover:border-prestige-gold hover:shadow-xl hover:shadow-prestige-gold/10 hover:-translate-y-1"
              >
                <span className="text-zinc-400 dark:text-zinc-500 font-black text-[9px] absolute top-3 left-1/2 -translate-x-1/2 tracking-tighter opacity-0 group-hover:opacity-100 transition-opacity uppercase">{TRANSLATIONS.dayLabel[lang]}</span>
                <span className="text-2xl sm:text-3xl font-heading text-ink dark:text-prestige-gold transition-colors pt-2">
                  #{publicDayNum}
                </span>
                {isCompleted && (
                  <div className="absolute -top-1.5 -right-1.5 bg-off-white dark:bg-midnight rounded-full p-0.5 z-10 shadow-sm">
                    <CheckCircle2 className="w-5 h-5 text-emerald fill-current dark:fill-midnight" strokeWidth={2.5} />
                  </div>
                )}
                <div className={`absolute bottom-2 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all duration-500 ${hasPlayed ? 'w-8 bg-zinc-300 dark:bg-zinc-700' : 'w-4 bg-zinc-300 dark:bg-zinc-800 group-hover:w-8 group-hover:bg-prestige-gold'}`} />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Archive;